import _ from 'lodash'

/**
 * Provider for {@link EpcReaderInterface}
 *
 * @name EpcReaderInterfaceProvider
 * @type {ngProvider}
 * @prop {number} realtimeInterval The number of milliseconds waited by a tag before being removed in `realtime` mode.
 */
export class EpcReaderInterface {
  static ngType = 'provider'

  constructor() {
    'ngInject'
    this.decodeQueueInterval = 100
    this.realtimeInterval = 800
  }

  /**
   * (sorta) Interface for EPC Readers
   *
   * Common public methods for devices that read EPCs.
   *
   * It is up to the device to manage connections and message brokering.
   * Due to limitations on `angular-resource`, the {@link TmrResource} definition
   * must also include the default values with `$defaultValues()`.
   *
   * Configurable by {@link EpcReaderInterfaceProvider}.
   *
   * @example
   * let MyDevice = TmrResource('MyDevice', {}, [], EpcReaderInterface.$defaultValues());
   * Object.assign(MyDevice.prototype, EpcReaderInterface);
   *
   * @name EpcReaderInterface
   * @type {ngFactory}
   * @interface
   * @prop {'persistent'|'realtime'} $mode When `persistent`, tags persist until `$clearEpcs` is called;
   *   when `realtime`, they will "fade away" after `realtimeInterval` has passed
   * @prop {array<string>} $epcs
   * @prop {array<Item>} $items
   * @prop {object} $itemsByEpc
   * @prop {object} $products keyed by upc
   * @prop {function} $clearEpcs
   * @see resource {@link Antenna} and service {@link WorkstationAntennas}
   */
  $get(
    $interval,
    $log,
    $rootScope,
    debounce,
    tmrTagTypes,
    TmrLocalStorage,
    Item
  ) {
    'ngInject'
    const decodeQueueInterval = this.decodeQueueInterval
    const realtimeInterval = this.realtimeInterval

    return {
      $start,
      $stop,
      $read,
      $write,
      $kill,
      $initQueues,
      $clearQueues,
      $decodeQueueProcess,
      $bulkDecode,
      $onBulkDecoded,
      $onEpcIgnored,
      $onEpcDecoded,
      $readEpc,
      $clearEpcs,
      $clearEpc,
      $getProduct,
      $defaultValues: () => ({
        $working: false,
        $started: false,
        $decode: true,
        $decoding: false,
        $loadProducts: true,
        $mode: TmrLocalStorage.get('epcReaderDefaultMode', 'persistent'),
        $epcs: [],
        $ignoredEpcs: [], // Epcs that can't be decoded.
        $epcTagTypes: {},
        $items: [],
        $itemsByEpc: {},
        $products: {},
        $clearEpcList: {},
        $decodeQueue: [],
        $itemService: Item,
        $readOperation: null,
      }),
    }

    /** @method EpcReaderInterface#$start */
    function $start() {
      throw new Error(
        'Class implementing EpcReaderInterface must define $start method'
      )
    }

    /** @method EpcReaderInterface#$stop */
    function $stop() {
      throw new Error(
        'Class implementing EpcReaderInterface must define $stop method'
      )
    }

    /** @method EpcReaderInterface#$read */
    function $read() {
      throw new Error(
        'Class implementing EpcReaderInterface must define $read method'
      )
    }

    /** @method EpcReaderInterface#$write */
    function $write() {
      throw new Error(
        'Class implementing EpcReaderInterface must define $write method'
      )
    }

    /** @method EpcReaderInterface#$kill */
    function $kill() {
      throw new Error(
        'Class implementing EpcReaderInterface must define $kill method'
      )
    }

    function $initQueues() {
      this.$decodeQueueInterval =
        this.$decodeQueueInterval ??
        $interval(
          () => this.$decodeQueueProcess(),
          decodeQueueInterval,
          0,
          false
        )
    }

    function $clearQueues() {
      $interval.cancel(this.$decodeQueueInterval)
      delete this.$decodeQueueInterval
      this.$decodeQueue.length = 0
    }

    function $decodeQueueProcess() {
      // Nothing to do.
      if (!this.$decodeQueue.length) return

      // Add the new epcs to the list.
      let epcs = [].concat(this.$decodeQueue)
      this.$decodeQueue.length = 0
      this.$epcs.push(...epcs)
      epcs.forEach(epc => $rootScope.$emit('epcread', epc, this))

      // Decode epcs and add the resulting items to the list.
      if (this.$decode) this.$bulkDecode(epcs)
    }

    function $bulkDecode(epcs) {
      this.$decoding = true
      return this.$itemService
        .bulkDecode(epcs)
        .$promise.then(
          items => this.$onBulkDecoded(epcs, items),
          () => epcs.forEach(epc => this.$onEpcIgnored(epc))
        )
        .finally(() => (this.$decoding = false))
    }

    function $onBulkDecoded(epcs, items) {
      // Ignore items that were already read with this epc.
      return epcs
        .filter(epc => this.$itemsByEpc[epc] === undefined)
        .forEach(epc => {
          // Epcs that the backend says must be ignored are null.
          if (items[epc] === null) this.$onEpcIgnored(epc)
          else this.$onEpcDecoded(items, epc)
        })
    }

    function $onEpcIgnored(epc) {
      if (this.$ignoredEpcs.indexOf(epc) === -1) {
        this.$ignoredEpcs.push(epc)
        $rootScope.$emit('epcignored', epc, this)
      }
    }

    function $onEpcDecoded(items, epc) {
      let item = this.$itemService.create(items[epc])
      // Read timestamp. Make it non-enumerable so it's discarded when the
      // item is converted to JSON.
      Object.defineProperty(item, '$readTime', {
        enumerable: false,
        value: new Date().getTime(),
      })
      this.$itemsByEpc[epc] = item
      // Avoid adding Items more than once.
      let codeProperty = tmrTagTypes[this.$epcTagTypes[epc]].codeProperty
      if (this.$items.find($item => $item[codeProperty] === epc) === undefined)
        this.$items.push(item)
      $rootScope.$emit('itemread', item, this, epc)
      if (this.$loadProducts) this.$getProduct(item)
    }

    /**
     * Reads an EPC from data
     *
     * Adds an Epc resource object to this.$epcs and returns it.
     * Note that these are called EPCs for historical reasons, but they may
     * actually any type defined in {@link tmrTagTypes}.
     *
     * @method EpcReaderInterface#$readEpc
     * @param {object} message
     * @param {object} message.data The epc to read or a TmrWebSocket message object.
     */
    function $readEpc({ data }) {
      let antenna = this.antennas.find(({ id }) => id == data.antennaIdDb)
      if (!antenna)
        throw new Error(`Message received from unrecognized antenna`)

      let tagType = antenna.rfidReader.$getTagType()
      if (!tmrTagTypes[tagType])
        throw new Error(`Unsupported tag type ${tagType}`)

      let code =
        typeof data === 'string'
          ? data
          : data[tmrTagTypes[tagType].codeProperty]
      if (!code)
        throw new Error(
          `Missing ${
            tmrTagTypes[tagType].codeProperty
          } while reading ${tagType} tag on Antenna ${antenna.$getLabel()}`
        )

      if (
        this.$epcs.indexOf(code) === -1 &&
        this.$decodeQueue.indexOf(code) === -1
      ) {
        $log.debug(
          `Reading ${tagType} tag ${code} on Antenna ${antenna.$getLabel()}`
        )
        this.$initQueues()
        this.$decodeQueue.push(code)
        this.$epcTagTypes[code] = tagType

        // Realtime mode means that epcs "fade away" if they are not seen for some time.
        if (
          this.$mode == 'realtime' &&
          this.$clearEpcList[code] === undefined
        ) {
          this.$clearEpcList[code] = debounce(realtimeInterval, () => {
            // Don't clear items when stopped.
            if (this.$started) this.$clearEpc(code)
          })
        }
      }

      // Debounce.
      ;(this.$clearEpcList[code] || _.noop)()

      return code
    }

    /**
     * Resets read epcs.
     *
     * @method EpcReaderInterface#$clearEpcs
     */
    function $clearEpcs() {
      this.$clearQueues()
      this.$epcs.length = 0
      this.$ignoredEpcs.length = 0
      this.$items.length = 0
      _.clearObject(this.$epcTagTypes)
      _.clearObject(this.$itemsByEpc)
      _.clearObject(this.$products)
    }

    /**
     * Resets a single read epc together with its product.
     *
     * @method EpcReaderInterface#$clearEpc
     * @param {string} epc
     * @param {string} [epcProperty='epc']
     */
    function $clearEpc(epc, epcProperty = 'epc') {
      let idx = this.$epcs.indexOf(epc)
      let queueIdx = this.$decodeQueue.indexOf(epc)
      let ignoredIdx = this.$ignoredEpcs.indexOf(epc)
      let itemIdx = this.$items.findIndex(item => item[epcProperty] == epc)

      if (idx > -1) this.$epcs.splice(idx, 1)
      if (queueIdx > -1) this.$decodeQueue.splice(queueIdx, 1)
      if (ignoredIdx > -1) this.$ignoredEpcs.splice(ignoredIdx, 1)

      if (itemIdx > -1) {
        let upc = this.$items[itemIdx].upc
        this.$items.splice(itemIdx, 1)

        // Delete product only if there are no other items with the same product.
        if (
          this.$products[upc] !== undefined &&
          this.$items.findIndex(item => item.upc == upc) == -1
        ) {
          delete this.$products[upc]
        }

        $rootScope.$emit('itemclear', this)
      }

      delete this.$epcTagTypes[epc]
      delete this.$itemsByEpc[epc]
      delete this.$clearEpcList[epc]
    }

    /**
     * Extracts a single product from an item.
     *
     * @method EpcReaderInterface#$getProduct
     */
    function $getProduct(item) {
      if (!item.upc) return
      if (this.$products[item.upc] === undefined)
        this.$products[item.upc] = item.$getProduct()
      // The product was already loaded by another item with the same upc.
      else item.$product = this.$products[item.upc]

      return this.$products[item.upc]
    }
  }
}
